import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuItem, TextField, Typography } from '@mui/material';
import moment from 'moment';
import SelectField from 'shared/components/formFields/select-field';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { ConnectorType } from 'features/connectors/domain/connector';
import useConnectorValidation from 'features/connectors/hooks/connector-validation-hook';
import ConnectorMonitoringAlert from 'features/connectors/views/components/connector-monitoring-alert';
import styles from 'features/connectors/views/connector-details/connector-information.module.scss';

const ConnectorInformation = () => {
  const { t } = useTranslation('connectors');

  const { connectorDetailsHook } = useConnectorsContextProvider();

  const { control, watch, formState } = useFormContext();

  const isViewing = connectorDetailsHook.viewingMode === 'viewing';
  const isEditing = connectorDetailsHook.viewingMode === 'editing';
  const isCreating = connectorDetailsHook.viewingMode === 'creation';

  const selectedConnector = connectorDetailsHook.currentSelectedConnector;
  const { validateType, isConnectorTypeValid } = useConnectorValidation();

  const connectorTypeOptions = Object.values(ConnectorType)
    .filter((x) => x !== ConnectorType.Unknown)
    .filter((x) => x !== ConnectorType.NedapOns)
    .map((type) => (
      <MenuItem key={type} value={type}>
        {t(`details.information.type.${type}`)}
      </MenuItem>
    ));

  const connectorOptions = {
    type: {
      required: t('info.requiredHelperText'),
      validate: validateType,
    },
    name: {
      required: t('info.requiredHelperText'),
    },
    note: {
      maxLength: {
        value: 255,
        message: t('info.maxLengthExceeded', { count: 255 }),
      },
    },
  };

  const currentFormType = watch('type');

  const renderNameField = () => (
    <Controller
      name="name"
      control={control}
      rules={connectorOptions.name}
      render={({ field }) => (
        <TextField
          {...field}
          id="nameInput"
          label={t('details.information.nameLabel')}
          variant="outlined"
          error={!!formState.errors.name}
          helperText={formState.errors.name ? (formState.errors.name.message as string) : undefined}
          fullWidth
        />
      )}
    />
  );

  const renderNoteField = () => (
    <Controller
      name="note"
      control={control}
      rules={connectorOptions.note}
      render={({ field }) => (
        <TextField
          {...field}
          slotProps={{
            htmlInput: {
              maxLength: 255,
            },
          }}
          id="noteInput"
          label={t('details.information.noteLabel')}
          variant="outlined"
          multiline
          error={!!formState.errors.note}
          helperText={formState.errors.note ? (formState.errors.note.message as string) : undefined}
          minRows={8}
          maxRows={12}
          fullWidth
        />
      )}
    />
  );

  const readOnlyField = (label: string, value: string, respectTextFormat = false) => (
    <div className={styles.informationItem}>
      <Typography variant="h5">{label}</Typography>
      <Typography variant="subtitle1" className={`${respectTextFormat && styles.textFormat}`}>
        {value}
      </Typography>
    </div>
  );

  const renderTitle = () => {
    switch (connectorDetailsHook.viewingMode) {
      case 'viewing':
        return selectedConnector?.name ?? '';
      case 'editing':
        return t('details.editConnectorTitle');
      case 'creation':
        return t('details.createConnectorTitle');
    }
  };

  const communicationDate = (lastCommunicationDate?: Date): string =>
    lastCommunicationDate
      ? t('details.information.statusLastCommunicationDateLabel', {
          lastCommunicationDate: moment(lastCommunicationDate).format('DD-MM-YYYY HH:mm').toString(),
        })
      : t('details.information.statusUnknownLabel');

  const renderViewingModeContent = () => {
    return (
      <>
        {selectedConnector?.type &&
          readOnlyField(t('details.information.typeLabel'), t(`details.information.type.${selectedConnector?.type}`))}
        {selectedConnector?.certificateExpiryDate && selectedConnector.type === ConnectorType.NedapOns && (
          <div className={styles.informationItem}>
            <Typography variant="h5">{t('details.information.certificateExpiryDate')}</Typography>
            <Typography variant="subtitle1">{moment(selectedConnector.certificateExpiryDate).format('DD-MM-YYYY').toString()}</Typography>
          </div>
        )}
        {selectedConnector?.type === ConnectorType.Vcare && (
          <div className={styles.informationItem}>{readOnlyField(t('details.information.nameLabel'), selectedConnector?.name ?? '')}</div>
        )}
        {selectedConnector?.note && readOnlyField(t('details.information.noteLabel'), selectedConnector.note, true)}
        {selectedConnector?.type === ConnectorType.Vcare && (
          <div className={styles.informationItem}>
            {readOnlyField(t('details.information.statusLabel'), communicationDate(selectedConnector.lastCommunicationDate))}
          </div>
        )}
      </>
    );
  };

  const renderEditingModeContent = () => {
    return (
      <>
        {selectedConnector?.type &&
          readOnlyField(t('details.information.typeLabel'), t(`details.information.type.${selectedConnector?.type}`))}

        {selectedConnector?.certificateExpiryDate && selectedConnector.type === ConnectorType.NedapOns && (
          <div className={styles.informationItem}>
            <Typography variant="h5">{t('details.information.certificateExpiryDate')}</Typography>
            <Typography variant="subtitle1">{moment(selectedConnector.certificateExpiryDate).format('DD-MM-YYYY').toString()}</Typography>
          </div>
        )}

        {currentFormType === ConnectorType.Vcare && <div className={styles.informationItem}>{renderNameField()}</div>}
        <div className={styles.informationItem}>{renderNoteField()}</div>
      </>
    );
  };

  const renderCreatingModeContent = () => {
    return (
      <>
        <div className={styles.informationItem}>
          <ViewingModeController
            viewingMode={connectorDetailsHook.viewingMode}
            name={`type`}
            rules={connectorOptions.type}
            control={control}
            label={t('details.information.typeLabel')}
            render={({ field }) => (
              <SelectField
                {...field}
                id="connectorType"
                variant="outlined"
                className="select-field"
                error={!!formState.errors.type}
                helperText={formState.errors.type ? (formState.errors.type.message as string) : undefined}
                label={t('details.information.typeLabel')}
                value={field.value === ConnectorType.Unknown ? '' : (field.value ?? '')}
              >
                {connectorTypeOptions.toSorted((a, b) => a.props.children.localeCompare(b.props.children))}
              </SelectField>
            )}
          />
        </div>
        {isConnectorTypeValid && currentFormType === ConnectorType.Vcare && (
          <>
            <div className={styles.informationItem}>{renderNameField()}</div>
            <div className={styles.informationItem}>{renderNoteField()}</div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={`${styles.container} ${isViewing && styles.viewing}`}>
      <Typography variant="h2" data-testid="connectorInformationName">
        {renderTitle()}
      </Typography>

      <ConnectorMonitoringAlert connector={selectedConnector} />

      {isViewing && renderViewingModeContent()}
      {isEditing && renderEditingModeContent()}
      {isCreating && renderCreatingModeContent()}
    </div>
  );
};

export default ConnectorInformation;
